//PATH: src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu,
  X,
  ChevronDown,
  Home,
  Briefcase,
  Users,
  Settings,
  Phone,
  Building2,
  Info, 
  Megaphone,
  FolderOpen,
  Wrench,
  FileText
} from "lucide-react";
import logo from "../assets/ce/Group.svg";

// Mock Link component for demo - replace with react-router-dom Link in your project
const Link = ({ to, children, onClick, className }) => (
  <a href={to} onClick={onClick} className={className}>
    {children}
  </a>
);

const Header = React.memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPagesOpen, setPagesOpen] = useState(false);
  const [isProjectsOpen, setProjectsOpen] = useState(false);
  const [isSectorsOpen, setSectorsOpen] = useState(false);


  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setPagesOpen(false);
    setProjectsOpen(false);
    setSectorsOpen(false);
  };

  // Toggle individual dropdowns
  const togglePagesDropdown = (e) => {
    e.stopPropagation();
    setPagesOpen(!isPagesOpen);
    setProjectsOpen(false);
    setSectorsOpen(false);
  };


  // Close dropdowns when clicking outside
  const closeDropdowns = (e) => {
    if (!e.target.closest(".dropdown")) {
      setPagesOpen(false);
      setProjectsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", closeDropdowns);
    return () => {
      document.removeEventListener("click", closeDropdowns);
    };
  }, []);

  const navigationItems = [
    { name: "HOME", to: "/", icon: Home },
    { name: "ABOUT", to: "/about", icon: Info },
    { name: "MEDIA", to: "/articles/press-release", icon: FileText },
    { name: "ARTICLE/BLOG", to: "/articles/blog", icon: FileText },
  ];

  const initiativeItems = [
    { name: "Youth in Politics Initiative", to: "/initiatives/youth-in-politics" },
    { name: "Youth in Cooperatives Initiative", to: "/initiatives/youth-in-cooperatives" },
    { name: "Youth Entrepreneurship Initiative", to: "/initiatives/youth-entrepreneurship" },
    { name: "Back 2 Community", to: "/initiatives/b2c" },
    { name: "Back 2 Roots", to: "/initiatives/b2r" }
  ];


  return (
    <div className="w-full relative">
      

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            
           {/* Logo */}
            <Link to="/" onClick={closeMobileMenu}>
              <motion.img 
                src={logo} 
                alt="Ray Infrastructures" 
                className="h-12 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              
              {/* Main Nav Items */}
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={item.name}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <Link
                      to={item.to}
                      className="group flex items-center space-x-2 text-gray-700 hover:text-[#87212e] font-medium text-sm transition-all duration-300 px-4 py-3 rounded-lg relative overflow-hidden"
                    >
                      <motion.div
                        className="absolute inset-0 bg-[#87212e]/5 scale-0 group-hover:scale-100 rounded-lg transition-transform duration-300 ease-out"
                      />
                      <motion.div
                        whileHover={{ rotate: [0, -5, 5, 0] }}
                        transition={{ duration: 0.3 }}
                        className="relative z-10"
                      >
                        <IconComponent className="w-4 h-4" />
                      </motion.div>
                      <span className="relative z-10">{item.name}</span>
                      <motion.div
                        className="absolute bottom-1 left-4 right-4 h-0.5 bg-[#87212e] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
                      />
                    </Link>
                  </motion.div>
                );
              })}

              {/* Initiatives Dropdown */}
              <div className="relative dropdown">
                <motion.button
                  onClick={togglePagesDropdown}
                  className="group flex items-center space-x-2 text-gray-700 hover:text-[#87212e] font-medium text-sm transition-all duration-300 px-4 py-3 rounded-lg relative overflow-hidden"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <motion.div
                    className="absolute inset-0 bg-[#87212e]/5 scale-0 group-hover:scale-100 rounded-lg transition-transform duration-300 ease-out"
                  />
                  <motion.div
                    whileHover={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10"
                  >
                    <Megaphone className="w-4 h-4" />
                  </motion.div>
                  <span className="relative z-10">INITIATIVES</span>
                  <motion.div
                    className="relative z-10"
                    animate={{ rotate: isPagesOpen ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                  <motion.div
                    className="absolute bottom-1 left-4 right-4 h-0.5 bg-[#87212e] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  />
                </motion.button>
                
                <AnimatePresence>
                  {isPagesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden"
                      style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.1))' }}
                    >
                      {initiativeItems.map((item, index) => (
                        <motion.div
                          key={item.name}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.2 }}
                        >
                          <Link 
                            to={item.to} 
                            onClick={closeMobileMenu}
                            className="group block px-4 py-3 text-sm text-gray-700 hover:bg-[#87212e]/5 hover:text-[#87212e] transition-all duration-200 relative"
                          >
                            <motion.div
                              className="absolute left-0 top-0 bottom-0 w-1 bg-[#87212e] scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ease-out origin-center"
                            />
                            <span className="ml-1">{item.name}</span>
                          </Link>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Contact Button */}
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="ml-4"
              >
                <Link 
                  to="/contact"
                  className="group flex items-center space-x-2 bg-[#87212e] hover:bg-[#6f1a25] text-white px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 relative overflow-hidden shadow-md hover:shadow-lg"
                >
                  <motion.div
                    className="absolute inset-0 bg-white/10 scale-0 group-hover:scale-100 transition-transform duration-300 ease-out origin-center"
                  />
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10"
                  >
                    <Phone className="w-4 h-4" />
                  </motion.div>
                  <span className="relative z-10">Contact Us</span>
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-[#87212e] hover:bg-gray-100 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-4 space-y-2">
                
                {/* Mobile Main Nav */}
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <motion.div
                      key={item.name}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <Link
                        to={item.to}
                        className="group flex items-center space-x-3 text-gray-700 hover:text-[#87212e] hover:bg-gray-50 px-3 py-3 rounded-lg font-medium transition-all duration-300 relative"
                        onClick={closeMobileMenu}
                      >
                        <motion.div
                          className="absolute left-0 top-0 bottom-0 w-1 bg-[#87212e] scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ease-out origin-center rounded-r"
                        />
                        <motion.div
                          whileHover={{ rotate: [0, -5, 5, 0] }}
                          transition={{ duration: 0.3 }}
                        >
                          <IconComponent className="w-5 h-5" />
                        </motion.div>
                        <span>{item.name}</span>
                      </Link>
                    </motion.div>
                  );
                })}

                {/* Mobile Initiatives */}
                <div className="space-y-1">
                  <motion.button
                    onClick={togglePagesDropdown}
                    className="group flex items-center justify-between w-full text-gray-700 hover:text-[#87212e] hover:bg-gray-50 px-3 py-3 rounded-lg font-medium transition-all duration-300 relative"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <motion.div
                      className="absolute left-0 top-0 bottom-0 w-1 bg-[#87212e] scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ease-out origin-center rounded-r"
                    />
                    <div className="flex items-center space-x-3">
                      <motion.div
                        whileHover={{ rotate: [0, -5, 5, 0] }}
                        transition={{ duration: 0.3 }}
                      >
                        <Megaphone className="w-5 h-5" />
                      </motion.div>
                      <span>Initiatives</span>
                    </div>
                    <motion.div
                      animate={{ rotate: isPagesOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </motion.button>
                  
                  <AnimatePresence>
                    {isPagesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-8 space-y-1"
                      >
                        {initiativeItems.map((item) => (
                          <motion.div
                            key={item.name}
                            whileHover={{ x: 5 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <Link 
                              to={item.to} 
                              onClick={closeMobileMenu} 
                              className="group block text-gray-600 hover:text-[#87212e] hover:bg-gray-50 px-3 py-2 rounded-lg transition-all duration-300 relative"
                            >
                              <motion.div
                                className="absolute left-0 top-0 bottom-0 w-1 bg-[#87212e] scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ease-out origin-center rounded-r"
                              />
                              <span className="ml-2">{item.name}</span>
                            </Link>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Contact */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <Link
                    to="/contact"
                    className="group flex items-center justify-center space-x-2 w-full bg-[#87212e] hover:bg-[#6f1a25] text-white px-4 py-3 rounded-lg font-medium transition-all duration-300 mt-4 relative overflow-hidden shadow-md hover:shadow-lg"
                    onClick={closeMobileMenu}
                  >
                    <motion.div
                      className="absolute inset-0 bg-white/10 scale-0 group-hover:scale-100 transition-transform duration-300 ease-out origin-center"
                    />
                    <motion.div
                      whileHover={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 0.3 }}
                      className="relative z-10"
                    >
                      <Phone className="w-5 h-5" />
                    </motion.div>
                    <span className="relative z-10">Contact Us</span>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
});

export default Header;